import { SyntheticEvent, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import * as actions from '@redux-actions/';

import {
    TextField,
    InputLabel,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    FormHelperText,
    Radio,
    InputAdornment,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import ArrowDown from '@images/svg-icons/arrowDown.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';

import { Controller, useFormContext } from 'react-hook-form';

import {
    GeographicInformationsPayload,
    PersonalDetailsFormStatus,
    PrimaryFormFields,
    PrimaryFormFieldsSettings,
    SecondaryFormFields,
    ThirdFormFields,
} from '@interfaces/myProfileInterfaces';

import { getPersonalDetailsCityAndState } from '@services/myProfileServices';

import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';

export interface Props {
    defaultFormValues: PrimaryFormFieldsSettings & SecondaryFormFields & ThirdFormFields;
}

const PersonalDetailsPrimaryForm = ({ defaultFormValues }: Props): JSX.Element => {
    const {
        register,
        watch,
        control,
        setValue,
        setError,
        formState: { errors, isSubmitting },
        clearErrors,
    } = useFormContext();

    const watchFirstName = watch('firstName');
    const watchLastName = watch('lastName');

    const initialFormStatus: PersonalDetailsFormStatus<PrimaryFormFieldsSettings> = {
        firstName: {
            focus: false,
            success: watchFirstName ? true : false,
        },
        lastName: {
            focus: false,
            success: watchLastName ? true : false,
        },
    };

    const [formFieldStatus, setFormFieldStatus] =
        useState<PersonalDetailsFormStatus<PrimaryFormFieldsSettings>>(initialFormStatus);

    /** The function below handles the onBlur, onChange e onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        !isSubmitting && clearErrors(event.name);

        if (
            ['firstName', 'lastName'].includes(event.name) &&
            e.type == 'blur' &&
            event.value.length &&
            (/\d/.test(event.value) || event.value.trim() === '')
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [event.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(event.name, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_${event.name.toUpperCase()}`
                    ),
                })
            );
        }
        event.value
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: {
                      success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                      focus:
                          e.type === 'blur'
                              ? false
                              : e.type === 'change'
                              ? true
                              : e.type === 'focus' && !errors[event.name]
                              ? true
                              : false,
                  },
              }),
              setValue(event.name, event.value))
            : !['billingAddressLine2', 'shippingAddressLine2'].includes(event.name)
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: { success: false, focus: false },
              }),
              setValue(event.name, ''),
              e.type === 'blur' &&
                  setError(event.name, {
                      type: 'required',
                      message: replaceLabelPlaceholder(
                          'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                          '{FORM_FIELD}',
                          getLabelFallback(
                              getErrorMessage(event.name as keyof PrimaryFormFields)
                          )?.toLowerCase()
                      ),
                  }))
            : setValue(event.name, '');
    };

    /** The function below returns the form field error message */

    const getErrorMessage = (field: keyof PrimaryFormFields): string =>
        `MYPROFILE_PERSONAL_DETAILS_FORM_${field.toUpperCase()}_LABEL`;

    return (
        <div className="primary-form">
            <div className="form-group">
                <TextField
                    {...register('firstName')}
                    required
                    className={`${
                        errors?.firstName?.message
                            ? 'form-field-error'
                            : formFieldStatus.firstName.success
                            ? 'form-field-success'
                            : ''
                    }`}
                    onChange={handleInputEvent}
                    onFocus={handleInputEvent}
                    onBlur={handleInputEvent}
                    name="firstName"
                    variant="outlined"
                    label={capitalizeFirstLetter(
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGFIRSTNAME_LABEL')
                    )}
                    helperText={errors?.firstName?.message as string}
                    InputProps={{
                        endAdornment: formFieldStatus.firstName.focus ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <CloseIcon
                                    className="cursor-pointer"
                                    onMouseDown={() => setValue('firstName', '')}
                                />
                            </InputAdornment>
                        ) : errors?.firstName?.message ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <ErrorIcon />
                            </InputAdornment>
                        ) : formFieldStatus.firstName.success ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <SuccessIcon />
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <TextField
                    {...register('lastName')}
                    required
                    className={`${
                        errors?.lastName?.message
                            ? 'form-field-error'
                            : formFieldStatus.lastName.success
                            ? 'form-field-success'
                            : ''
                    }`}
                    onChange={handleInputEvent}
                    onFocus={handleInputEvent}
                    onBlur={handleInputEvent}
                    name="lastName"
                    variant="outlined"
                    label={capitalizeFirstLetter(
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGLASTNAME_LABEL')
                    )}
                    helperText={errors?.lastName?.message as string}
                    InputProps={{
                        endAdornment: formFieldStatus.lastName.focus ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <CloseIcon
                                    className="cursor-pointer"
                                    onMouseDown={() => setValue('lastName', '')}
                                />
                            </InputAdornment>
                        ) : errors?.lastName?.message ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <ErrorIcon />
                            </InputAdornment>
                        ) : formFieldStatus.lastName.success ? (
                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                <SuccessIcon />
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </div>
        </div>
    );
};
export default PersonalDetailsPrimaryForm;
